import { cloneProductDeliveryAddressPolicy, LEGACY_PRODUCT_DELIVERY_REQUIRED_FIELDS, type ProductDeliveryAddressPolicyV1 } from "@/app/commerce/products/productDeliveryAddressPolicy";

/**
 * Client-visible online product store for one marketplace location.
 *
 * The store is its own domain: it never joins booking data (services,
 * offerings, calendar) and it publishes only client-facing product fields.
 * `SpecialistWorkplace.offersOnlineProducts` stays the single capability
 * gate; this catalog supplies the published inventory for locations where
 * that flag is true. External references (Fresha) informed the field split
 * between client-visible and internal data, not naming or presentation.
 *
 * Categories are business-defined navigation filters only: they never
 * change pricing, availability, or ordering semantics. The localized
 * "All products" tab is automatic UI, never a stored category.
 */

export interface LocationStoreProductPrice {
  amount: number;
  currency: string;
}

export interface LocationStoreProductSale {
  /** Provider-authored customer-facing promotion copy. */
  offerLabel: string;
  /** Original unit price before the exact current product price was applied. */
  originalPrice: LocationStoreProductPrice;
}

export interface LocationStoreTaxSnapshot {
  taxPolicyId: string;
  version: string;
  treatment: "included" | "exclusive" | "not-applicable";
  rateBasisPoints: number | null;
}

export interface LocationStoreOnlinePublication {
  isPublished: boolean;
  isPurchasable: boolean;
}

export type LocationStoreProductInventory =
  | {
      kind: "finite";
      inventoryVersion: string;
      availableQuantity: number;
      maximumQuantityPerOrder: number;
    }
  | {
      kind: "unlimited";
      inventoryVersion: string;
      maximumQuantityPerOrder: number;
    };

export interface LocationStoreCategory {
  categoryId: string;
  /** Business-authored display name, written in the business's language. */
  name: string;
}

export interface LocationStoreProduct {
  productId: string;
  /** Exact definition revision used by the product-commerce handoff. */
  productVersion: string;
  name: string;
  brand: string;
  /** Must reference a declared category of the same store. */
  categoryId: string;
  /** Client-facing pack-size copy, e.g. "50 ml"; null when not applicable. */
  measure: string | null;
  price: LocationStoreProductPrice;
  /** Null means the provider published no active compare-at evidence. */
  sale: LocationStoreProductSale | null;
  imageSrc: string;
  imageAlt: string;
  /** Retail and online gates stay explicit and never derive from stock. */
  isRetailSaleEnabled: boolean;
  onlinePublication: LocationStoreOnlinePublication;
  inventory: LocationStoreProductInventory;
  tax: LocationStoreTaxSnapshot;
}

export interface LocationStoreDeliveryOption {
  optionId: string;
  optionVersion: string;
  /** Business-authored option name, e.g. "Livrare standard (curier)". */
  name: string;
  /** Flat fee decided by the business; never derived from cart value. */
  price: LocationStoreProductPrice;
  tax: LocationStoreTaxSnapshot;
}

export interface LocationStorePickupLocation {
  pickupLocationId: string;
  pickupLocationVersion: string;
  name: string;
  address: string;
  isEnabled: boolean;
}

/**
 * Fulfillment is store-owned configuration: pickup happens at the store's
 * own location, delivery uses business-authored flat-fee options. At least
 * one path must be enabled for a published store.
 */
export interface LocationStoreFulfillmentConfig {
  fulfillmentVersion: string;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  pickupLocations: readonly LocationStorePickupLocation[];
  deliveryOptions: readonly LocationStoreDeliveryOption[];
  /** Omitted preserves the legacy required fields; an explicit policy is exact. */
  deliveryAddressPolicy?: ProductDeliveryAddressPolicyV1;
}

export interface LocationStorePolicySnapshot {
  policyId: string;
  version: string;
  content: string;
}

export interface LocationStoreOnlinePurchase {
  isPublished: boolean;
  isPurchasable: boolean;
  /**
   * `ready` authorizes only a versioned frontend handoff. It does not prove
   * that a payment method or purchase mutation is currently available.
   */
  purchaseProviderState: "ready" | "unsupported";
  providerId: string | null;
}

export interface LocationStore {
  locationId: string;
  storeVersion: string;
  catalogVersion: string;
  clientSnapshot: {
    name: string;
    address: string;
    imageSrc: string | null;
    imageAlt: string;
  };
  onlinePurchase: LocationStoreOnlinePurchase;
  categories: readonly LocationStoreCategory[];
  products: readonly LocationStoreProduct[];
  fulfillment: LocationStoreFulfillmentConfig;
  returnPolicy: LocationStorePolicySnapshot | null;
  otherInformation: LocationStorePolicySnapshot | null;
}

const PRODUCT_TAX_INCLUDED: LocationStoreTaxSnapshot = {
  taxPolicyId: "tax-glow-retail-standard",
  version: "tax-glow-retail-standard-v1",
  treatment: "included",
  rateBasisPoints: 1_900,
};

const LOCATION_STORE_FIXTURES: readonly LocationStore[] = [
  {
    locationId: "venue-glow-room",
    storeVersion: "store-glow-v1",
    catalogVersion: "product-catalog-glow-v2",
    clientSnapshot: {
      name: "Glow Room",
      address: "Strada Eugeniu de Savoya 12, Timișoara",
      imageSrc:
        "https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?auto=format&fit=crop&w=960&q=82",
      imageAlt: "Glow Room interior",
    },
    onlinePurchase: {
      isPublished: true,
      isPurchasable: true,
      purchaseProviderState: "ready",
      providerId: "frontend-product-provider-glow-v1",
    },
    fulfillment: {
      fulfillmentVersion: "fulfillment-glow-v2",
      pickupEnabled: true,
      deliveryEnabled: true,
      // RO and GB form requiredness was directly operated in Fresha Client;
      // the existing remaining preview countries retain conservative defaults.
      // This is a frontend Store publication, not universal postal authority.
      deliveryAddressPolicy: {
        schemaVersion: 1,
        policyId: "frontend-glow-delivery-address-policy",
        policyVersion: "frontend-glow-delivery-address-policy-v1",
        countries: ["RO", "GB", "UA", "MD", "BG", "RS", "HU", "HR", "SI", "BA", "AT", "BE", "CY", "CZ", "DK", "EE"].map(countryCode => ({
          countryCode,
          requiredFields: countryCode === "RO" || countryCode === "GB"
            ? ["addressLine1", "city", "countryCode"] as const
            : LEGACY_PRODUCT_DELIVERY_REQUIRED_FIELDS,
        })),
      },
      pickupLocations: [
        {
          pickupLocationId: "pickup-glow-room",
          pickupLocationVersion: "pickup-glow-room-v1",
          name: "Glow Room",
          address: "Strada Eugeniu de Savoya 12, Timișoara",
          isEnabled: true,
        },
      ],
      deliveryOptions: [
        {
          optionId: "delivery-glow-standard",
          optionVersion: "delivery-glow-standard-v1",
          name: "Livrare standard (curier)",
          price: { amount: 19, currency: "RON" },
          tax: {
            taxPolicyId: "tax-glow-delivery-standard",
            version: "tax-glow-delivery-standard-v1",
            treatment: "included",
            rateBasisPoints: 1_900,
          },
        },
        {
          optionId: "delivery-glow-express",
          optionVersion: "delivery-glow-express-v1",
          name: "Livrare rapidă (24h)",
          price: { amount: 34, currency: "RON" },
          tax: {
            taxPolicyId: "tax-glow-delivery-express",
            version: "tax-glow-delivery-express-v1",
            treatment: "included",
            rateBasisPoints: 1_900,
          },
        },
      ],
    },
    returnPolicy: {
      policyId: "return-policy-glow",
      version: "return-policy-glow-v1",
      content:
        "Produsele pot fi returnate în termen de 14 zile de la primire, în ambalajul original, sigilat și nefolosit. Costul transportului de retur nu este rambursat.",
    },
    otherInformation: {
      policyId: "store-information-glow",
      version: "store-information-glow-v1",
      content:
        "Comenzile sunt pregătite în 1-2 zile lucrătoare. Pentru ridicare din salon te rugăm să aștepți confirmarea „Gata de ridicare”.",
    },
    categories: [
      { categoryId: "category-glow-skincare", name: "Îngrijire ten" },
      { categoryId: "category-glow-makeup", name: "Machiaj" },
      { categoryId: "category-glow-gift-sets", name: "Seturi cadou" },
    ],
    products: [
      {
        productId: "product-glow-vitamin-c-serum",
        productVersion: "product-glow-vitamin-c-serum-v2",
        name: "Ser iluminator cu vitamina C",
        brand: "Lumière Lab",
        categoryId: "category-glow-skincare",
        measure: "30 ml",
        price: { amount: 189, currency: "RON" },
        sale: {
          offerLabel: "Reducere 10%",
          originalPrice: { amount: 210, currency: "RON" },
        },
        imageSrc:
          "https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?auto=format&fit=crop&w=640&q=82",
        imageAlt: "Ser iluminator cu vitamina C",
        isRetailSaleEnabled: true,
        onlinePublication: {
          isPublished: true,
          isPurchasable: true,
        },
        inventory: {
          kind: "finite",
          inventoryVersion: "inventory-glow-vitamin-c-serum-v1",
          availableQuantity: 6,
          maximumQuantityPerOrder: 4,
        },
        tax: PRODUCT_TAX_INCLUDED,
      },
      {
        productId: "product-glow-hyaluronic-cream",
        productVersion: "product-glow-hyaluronic-cream-v1",
        name: "Cremă hidratantă cu acid hialuronic",
        brand: "Lumière Lab",
        categoryId: "category-glow-skincare",
        measure: "50 ml",
        price: { amount: 165, currency: "RON" },
        sale: null,
        imageSrc:
          "https://images.unsplash.com/photo-1598440947619-2c35fc9aa908?auto=format&fit=crop&w=640&q=82",
        imageAlt: "Cremă hidratantă cu acid hialuronic",
        isRetailSaleEnabled: true,
        onlinePublication: {
          isPublished: true,
          isPurchasable: true,
        },
        inventory: {
          kind: "finite",
          inventoryVersion: "inventory-glow-hyaluronic-cream-v1",
          availableQuantity: 2,
          maximumQuantityPerOrder: 2,
        },
        tax: PRODUCT_TAX_INCLUDED,
      },
      {
        productId: "product-glow-rose-face-oil",
        productVersion: "product-glow-rose-face-oil-v1",
        name: "Ulei facial regenerant cu trandafir",
        brand: "Botanica Skin",
        categoryId: "category-glow-skincare",
        measure: "30 ml",
        price: { amount: 132, currency: "RON" },
        sale: null,
        imageSrc:
          "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=640&q=82",
        imageAlt: "Ulei facial regenerant cu trandafir",
        isRetailSaleEnabled: true,
        onlinePublication: {
          isPublished: true,
          isPurchasable: true,
        },
        inventory: {
          kind: "finite",
          inventoryVersion: "inventory-glow-rose-face-oil-v1",
          availableQuantity: 0,
          maximumQuantityPerOrder: 3,
        },
        tax: PRODUCT_TAX_INCLUDED,
      },
      {
        productId: "product-glow-chamomile-micellar-water",
        productVersion: "product-glow-chamomile-micellar-water-v1",
        name: "Apă micelară cu extract de mușețel",
        brand: "Botanica Skin",
        categoryId: "category-glow-skincare",
        measure: "400 ml",
        price: { amount: 58, currency: "RON" },
        sale: null,
        imageSrc:
          "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=640&q=82",
        imageAlt: "Apă micelară cu extract de mușețel",
        isRetailSaleEnabled: true,
        onlinePublication: {
          isPublished: true,
          isPurchasable: true,
        },
        inventory: {
          kind: "unlimited",
          inventoryVersion: "inventory-glow-chamomile-water-v1",
          maximumQuantityPerOrder: 10,
        },
        tax: PRODUCT_TAX_INCLUDED,
      },
      {
        productId: "product-glow-revitalizing-night-mask",
        productVersion: "product-glow-revitalizing-night-mask-v1",
        name: "Mască de noapte revitalizantă",
        brand: "Botanica Skin",
        categoryId: "category-glow-skincare",
        measure: "50 ml",
        price: { amount: 118, currency: "RON" },
        sale: null,
        imageSrc:
          "https://images.unsplash.com/photo-1585232004423-244e0e6904e3?auto=format&fit=crop&w=640&q=82",
        imageAlt: "Mască de noapte revitalizantă",
        isRetailSaleEnabled: true,
        onlinePublication: {
          isPublished: true,
          isPurchasable: true,
        },
        inventory: {
          kind: "finite",
          inventoryVersion: "inventory-glow-night-mask-v1",
          availableQuantity: 8,
          maximumQuantityPerOrder: 5,
        },
        tax: PRODUCT_TAX_INCLUDED,
      },
      {
        productId: "product-glow-nude-eyeshadow-palette",
        productVersion: "product-glow-nude-eyeshadow-palette-v1",
        name: "Paletă farduri de pleoape Nude Season",
        brand: "Velvet Beauty",
        categoryId: "category-glow-makeup",
        measure: "12 x 1,5 g",
        price: { amount: 145, currency: "RON" },
        sale: null,
        imageSrc:
          "https://images.unsplash.com/photo-1596462502278-27bfdc403348?auto=format&fit=crop&w=640&q=82",
        imageAlt: "Paletă farduri de pleoape Nude Season",
        isRetailSaleEnabled: true,
        onlinePublication: {
          isPublished: true,
          isPurchasable: true,
        },
        inventory: {
          kind: "finite",
          inventoryVersion: "inventory-glow-nude-palette-v1",
          availableQuantity: 5,
          maximumQuantityPerOrder: 3,
        },
        tax: PRODUCT_TAX_INCLUDED,
      },
      {
        productId: "product-glow-hydrating-cream-lipstick",
        productVersion: "product-glow-hydrating-cream-lipstick-v1",
        name: "Ruj cremos hidratant",
        brand: "Velvet Beauty",
        categoryId: "category-glow-makeup",
        measure: "4,5 g",
        price: { amount: 76, currency: "RON" },
        sale: null,
        imageSrc:
          "https://images.unsplash.com/photo-1631729371254-42c2892f0e6e?auto=format&fit=crop&w=640&q=82",
        imageAlt: "Ruj cremos hidratant",
        isRetailSaleEnabled: true,
        onlinePublication: {
          isPublished: true,
          isPurchasable: true,
        },
        inventory: {
          kind: "finite",
          inventoryVersion: "inventory-glow-lipstick-v1",
          availableQuantity: 12,
          maximumQuantityPerOrder: 6,
        },
        tax: PRODUCT_TAX_INCLUDED,
      },
      {
        productId: "product-glow-mini-ritual-gift-set",
        productVersion: "product-glow-mini-ritual-gift-set-v1",
        name: "Set cadou mini-rituale de îngrijire",
        brand: "Lumière Lab",
        categoryId: "category-glow-gift-sets",
        measure: null,
        price: { amount: 210, currency: "RON" },
        sale: null,
        imageSrc:
          "https://images.unsplash.com/photo-1571781926291-c477ebfd024b?auto=format&fit=crop&w=640&q=82",
        imageAlt: "Set cadou mini-rituale de îngrijire",
        isRetailSaleEnabled: true,
        onlinePublication: {
          isPublished: true,
          isPurchasable: true,
        },
        inventory: {
          kind: "finite",
          inventoryVersion: "inventory-glow-gift-set-v1",
          availableQuantity: 4,
          maximumQuantityPerOrder: 2,
        },
        tax: PRODUCT_TAX_INCLUDED,
      },
    ],
  },
];

/**
 * A store with an undeclared product category, a duplicate ID, or an empty
 * category is invalid fixture data and must fail loudly at module load,
 * mirroring the specialist catalog's drift assertions.
 */
function assertLocationStoreFixtures(stores: readonly LocationStore[]) {
  for (const store of stores) {
    if (
      !store.locationId.trim() ||
      !store.storeVersion.trim() ||
      !store.catalogVersion.trim() ||
      !store.clientSnapshot.name.trim() ||
      !store.clientSnapshot.address.trim() ||
      !store.clientSnapshot.imageAlt.trim() ||
      (store.clientSnapshot.imageSrc !== null &&
        !store.clientSnapshot.imageSrc.trim())
    ) {
      throw new Error("Location store identity and revisions are required.");
    }

    if (
      !store.onlinePurchase.isPublished ||
      !store.onlinePurchase.isPurchasable ||
      store.onlinePurchase.purchaseProviderState !== "ready" ||
      !store.onlinePurchase.providerId?.trim()
    ) {
      throw new Error(
        `Published store ${store.locationId} must provide exact online-purchase evidence.`,
      );
    }

    const categoryIds = new Set<string>();

    for (const category of store.categories) {
      if (categoryIds.has(category.categoryId)) {
        throw new Error(
          `Duplicate store category ${category.categoryId} for ${store.locationId}.`,
        );
      }

      categoryIds.add(category.categoryId);
    }

    const productIds = new Set<string>();
    const populatedCategoryIds = new Set<string>();
    let storeCurrency: string | null = null;

    for (const product of store.products) {
      if (productIds.has(product.productId)) {
        throw new Error(
          `Duplicate store product ${product.productId} for ${store.locationId}.`,
        );
      }

      productIds.add(product.productId);

      if (
        !product.productVersion.trim() ||
        !product.isRetailSaleEnabled ||
        !product.onlinePublication.isPublished ||
        !product.onlinePublication.isPurchasable
      ) {
        throw new Error(
          `Published product ${product.productId} for ${store.locationId} has incomplete purchase evidence.`,
        );
      }

      storeCurrency ??= product.price.currency;

      if (product.price.currency !== storeCurrency) {
        throw new Error(`Store ${store.locationId} mixes product currencies.`);
      }

      if (!categoryIds.has(product.categoryId)) {
        throw new Error(
          `Store product ${product.productId} references undeclared category ${product.categoryId}.`,
        );
      }

      if (
        !Number.isFinite(product.price.amount) ||
        product.price.amount < 0 ||
        !/^[A-Z]{3}$/.test(product.price.currency)
      ) {
        throw new Error(
          `Store product ${product.productId} has an invalid price.`,
        );
      }

      if (
        product.sale !== null &&
        (product.sale.offerLabel !== product.sale.offerLabel.trim() ||
          product.sale.offerLabel.length === 0 ||
          product.sale.offerLabel.length > 5_000 ||
          !Number.isFinite(product.sale.originalPrice.amount) ||
          product.sale.originalPrice.amount <= product.price.amount ||
          product.sale.originalPrice.currency !== product.price.currency)
      ) {
        throw new Error(
          `Store product ${product.productId} has invalid sale evidence.`,
        );
      }

      if (
        !product.inventory.inventoryVersion.trim() ||
        !Number.isSafeInteger(product.inventory.maximumQuantityPerOrder) ||
        product.inventory.maximumQuantityPerOrder < 1 ||
        (product.inventory.kind === "finite" &&
          (!Number.isSafeInteger(product.inventory.availableQuantity) ||
            product.inventory.availableQuantity < 0))
      ) {
        throw new Error(
          `Store product ${product.productId} has invalid inventory evidence.`,
        );
      }

      assertLocationStoreTaxSnapshot(
        product.tax,
        `product ${product.productId}`,
      );
      populatedCategoryIds.add(product.categoryId);
    }

    for (const category of store.categories) {
      if (!populatedCategoryIds.has(category.categoryId)) {
        throw new Error(
          `Store category ${category.categoryId} for ${store.locationId} has no products.`,
        );
      }
    }

    const { fulfillment } = store;

    if (Object.hasOwn(fulfillment, "deliveryAddressPolicy") &&
      !cloneProductDeliveryAddressPolicy(fulfillment.deliveryAddressPolicy)) {
      throw new Error(`Store ${store.locationId} has an invalid delivery address policy.`);
    }

    if (!fulfillment.fulfillmentVersion.trim()) {
      throw new Error(`Store ${store.locationId} has no fulfillment version.`);
    }

    if (!fulfillment.pickupEnabled && !fulfillment.deliveryEnabled) {
      throw new Error(
        `Store ${store.locationId} must enable at least one fulfillment path.`,
      );
    }

    if (
      fulfillment.deliveryEnabled &&
      fulfillment.deliveryOptions.length === 0
    ) {
      throw new Error(
        `Store ${store.locationId} enables delivery without delivery options.`,
      );
    }

    if (
      fulfillment.pickupEnabled &&
      !fulfillment.pickupLocations.some((location) => location.isEnabled)
    ) {
      throw new Error(
        `Store ${store.locationId} enables pickup without an enabled pickup location.`,
      );
    }

    const pickupLocationIds = new Set<string>();

    for (const pickupLocation of fulfillment.pickupLocations) {
      if (
        pickupLocationIds.has(pickupLocation.pickupLocationId) ||
        !pickupLocation.pickupLocationId.trim() ||
        !pickupLocation.pickupLocationVersion.trim() ||
        !pickupLocation.name.trim() ||
        !pickupLocation.address.trim()
      ) {
        throw new Error(
          `Store ${store.locationId} has an invalid or duplicate pickup location.`,
        );
      }

      pickupLocationIds.add(pickupLocation.pickupLocationId);
    }

    const deliveryOptionIds = new Set<string>();

    for (const option of fulfillment.deliveryOptions) {
      if (deliveryOptionIds.has(option.optionId)) {
        throw new Error(
          `Duplicate delivery option ${option.optionId} for ${store.locationId}.`,
        );
      }

      deliveryOptionIds.add(option.optionId);

      if (!option.name.trim()) {
        throw new Error(
          `Delivery option ${option.optionId} for ${store.locationId} has no name.`,
        );
      }

      if (
        !option.optionVersion.trim() ||
        !Number.isFinite(option.price.amount) ||
        option.price.amount < 0 ||
        !/^[A-Z]{3}$/.test(option.price.currency)
      ) {
        throw new Error(
          `Delivery option ${option.optionId} for ${store.locationId} has invalid purchase evidence.`,
        );
      }

      if (storeCurrency && option.price.currency !== storeCurrency) {
        throw new Error(
          `Store ${store.locationId} mixes fulfillment and product currencies.`,
        );
      }

      assertLocationStoreTaxSnapshot(
        option.tax,
        `delivery option ${option.optionId}`,
      );
    }

    for (const policy of [store.returnPolicy, store.otherInformation]) {
      if (
        policy &&
        (!policy.policyId.trim() ||
          !policy.version.trim() ||
          !policy.content.trim())
      ) {
        throw new Error(
          `Store ${store.locationId} has an invalid client policy snapshot.`,
        );
      }
    }
  }
}

function assertLocationStoreTaxSnapshot(
  tax: LocationStoreTaxSnapshot,
  subject: string,
) {
  if (
    !tax.taxPolicyId.trim() ||
    !tax.version.trim() ||
    (tax.treatment !== "included" &&
      tax.treatment !== "exclusive" &&
      tax.treatment !== "not-applicable") ||
    (tax.treatment === "not-applicable"
      ? tax.rateBasisPoints !== null
      : !Number.isSafeInteger(tax.rateBasisPoints) ||
        (tax.rateBasisPoints ?? -1) < 0 ||
        (tax.rateBasisPoints ?? 10_001) > 10_000)
  ) {
    throw new Error(`Invalid tax snapshot for ${subject}.`);
  }
}

assertLocationStoreFixtures(LOCATION_STORE_FIXTURES);

function encodeRouteSegment(value: string) {
  const normalized = value.trim();

  if (!normalized) {
    throw new Error("Location store route segments cannot be empty.");
  }

  return encodeURIComponent(normalized);
}

/** Exact-ID lookup; a location without published products has no store. */
export function getLocationStore(locationId: string): LocationStore | null {
  return (
    LOCATION_STORE_FIXTURES.find((store) => store.locationId === locationId) ??
    null
  );
}

/** Exact-ID category lookup within one store; unknown IDs resolve to null. */
export function getLocationStoreCategory(
  store: LocationStore,
  categoryId: string,
): LocationStoreCategory | null {
  return (
    store.categories.find((category) => category.categoryId === categoryId) ??
    null
  );
}

/**
 * Category filtering is navigation only: `null` is the automatic
 * "All products" view and returns the complete published catalog.
 */
export function filterLocationStoreProducts(
  store: LocationStore,
  categoryId: string | null,
): readonly LocationStoreProduct[] {
  const publishedProducts = store.products.filter(
    isLocationStoreProductPublished,
  );

  if (categoryId === null) {
    return publishedProducts;
  }

  return publishedProducts.filter(
    (product) => product.categoryId === categoryId,
  );
}

/**
 * Shared publication boundary for every client entry into Product Commerce.
 * A capability flag alone is never enough: the exact location Store must be
 * published, purchasable, provider-ready, and expose at least one published
 * product before any route or capability card may link to it.
 */
export function getOnlinePurchasableLocationStore(
  locationId: string,
): LocationStore | null {
  const store = getLocationStore(locationId);

  return store &&
    store.onlinePurchase.isPublished &&
    store.onlinePurchase.isPurchasable &&
    store.onlinePurchase.purchaseProviderState === "ready" &&
    Boolean(store.onlinePurchase.providerId) &&
    filterLocationStoreProducts(store, null).length > 0
    ? store
    : null;
}

/** Online visibility is independent from stock and purchase eligibility. */
export function isLocationStoreProductPublished(product: LocationStoreProduct) {
  return product.isRetailSaleEnabled && product.onlinePublication.isPublished;
}

/**
 * The maximum applies synchronously to every cart mutation. Sold-out,
 * unpublished, and retail-disabled products return zero and can never be
 * revived by stale browser storage.
 */
export function getLocationStoreProductMaximumQuantity(
  product: LocationStoreProduct,
) {
  if (
    !isLocationStoreProductPublished(product) ||
    !product.onlinePublication.isPurchasable
  ) {
    return 0;
  }

  if (product.inventory.kind === "unlimited") {
    return product.inventory.maximumQuantityPerOrder;
  }

  return Math.min(
    product.inventory.availableQuantity,
    product.inventory.maximumQuantityPerOrder,
  );
}

export function isLocationStoreProductPurchasable(
  product: LocationStoreProduct,
) {
  return getLocationStoreProductMaximumQuantity(product) > 0;
}

export function buildLocationStorePath(locationId: string) {
  return `/locations/${encodeRouteSegment(locationId)}/store`;
}

export interface LocationStoreCartLine {
  product: LocationStoreProduct;
  quantity: number;
}

export interface LocationStoreDeliveryOptionSelection {
  optionId: string;
}

/**
 * Cart money math is pure and store-agnostic: the subtotal sums quantity ×
 * retail price, the delivery fee is the selected flat option or zero for
 * pickup, and every amount stays in the store's single currency.
 */
export function summarizeLocationStoreCart(
  lines: readonly LocationStoreCartLine[],
  deliveryOption: LocationStoreDeliveryOption | null = null,
) {
  const itemCount = lines.reduce((total, line) => total + line.quantity, 0);
  const subtotalAmount = lines.reduce(
    (total, line) => total + line.quantity * line.product.price.amount,
    0,
  );
  const deliveryFeeAmount = deliveryOption?.price.amount ?? 0;
  const currency =
    lines[0]?.product.price.currency ?? deliveryOption?.price.currency ?? "RON";

  return {
    currency,
    deliveryFeeAmount,
    itemCount,
    subtotalAmount,
    totalAmount: subtotalAmount + deliveryFeeAmount,
  };
}
